import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Trash2, Plus, Save, Home, Tag, LayoutGrid, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBilingual = () => ({ ar: '', en: '' });

const emptyState = () => ({
  hero: {
    title: emptyBilingual(),
    subtitle: emptyBilingual(),
    cta_text: emptyBilingual(),
    cta_link: '/subscriptions',
    doctor_photo_url: '',
  },
  offer: {
    enabled: true,
    badge: emptyBilingual(),
    headline: emptyBilingual(),
    description: emptyBilingual(),
    cta_text: emptyBilingual(),
    only_label: emptyBilingual(),
    period_label: emptyBilingual(),
    cta_link: '/subscriptions',
    price: '',
    original_price: '',
  },
  sections: {
    services_heading: emptyBilingual(),
    why_heading: emptyBilingual(),
    why_subtitle: emptyBilingual(),
  },
  banners: [],
});

// Small helper: bilingual text field (AR + EN side-by-side)
const BilingualField = ({ label, value, onChange, testIdBase, textarea = false }) => {
  const Comp = textarea ? Textarea : Input;
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Arabic (AR)</Label>
          <Comp
            dir="rtl"
            value={value?.ar || ''}
            onChange={(e) => onChange({ ...(value || {}), ar: e.target.value })}
            data-testid={`${testIdBase}-ar`}
            placeholder="النص بالعربية"
            className="text-right"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">English (EN)</Label>
          <Comp
            value={value?.en || ''}
            onChange={(e) => onChange({ ...(value || {}), en: e.target.value })}
            data-testid={`${testIdBase}-en`}
            placeholder="Text in English"
          />
        </div>
      </div>
    </div>
  );
};

export const AdminHomepageView = () => {
  const { t } = useLanguage();
  const [state, setState] = useState(emptyState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/homepage`);
        // Merge with empty state to guarantee shape
        const base = emptyState();
        setState({
          hero: { ...base.hero, ...(r.data?.hero || {}) },
          offer: { ...base.offer, ...(r.data?.offer || {}) },
          sections: { ...base.sections, ...(r.data?.sections || {}) },
          banners: Array.isArray(r.data?.banners) ? r.data.banners : [],
        });
      } catch {
        toast.error(t('تعذّر تحميل محتوى الصفحة', 'Failed to load homepage content'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const updateHero = (key, val) => setState((s) => ({ ...s, hero: { ...s.hero, [key]: val } }));
  const updateOffer = (key, val) => setState((s) => ({ ...s, offer: { ...s.offer, [key]: val } }));
  const updateSection = (key, val) => setState((s) => ({ ...s, sections: { ...s.sections, [key]: val } }));

  const addBanner = () => {
    setState((s) => ({
      ...s,
      banners: [
        ...s.banners,
        {
          id: `new-${Date.now()}`,
          title: emptyBilingual(),
          subtitle: emptyBilingual(),
          image_url: '',
          cta_link: '',
          order: s.banners.length,
          active: true,
        },
      ],
    }));
  };

  const updateBanner = (idx, key, val) => {
    setState((s) => {
      const copy = [...s.banners];
      copy[idx] = { ...copy[idx], [key]: val };
      return { ...s, banners: copy };
    });
  };

  const removeBanner = (idx) => {
    setState((s) => ({ ...s, banners: s.banners.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/homepage`, state, { withCredentials: true });
      toast.success(t('تم حفظ محتوى الصفحة الرئيسية', 'Homepage content saved successfully'));
    } catch (e) {
      toast.error(t('حدث خطأ أثناء الحفظ', 'Failed to save changes'));
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center text-slate-500 py-12" data-testid="homepage-cms-loading">
        {t('جاري التحميل...', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-homepage-cms">
      {/* Header + Save */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
            <Home size={22} className="text-[#1565c0]" />
            {t('محتوى الصفحة الرئيسية', 'Homepage Content')}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t(
              'قم بتحرير النصوص والصور والعروض التي تظهر للزوار على الصفحة الرئيسية',
              'Edit the text, images and offers shown to visitors on the homepage',
            )}
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1565c0] hover:bg-[#0d47a1] text-white"
          data-testid="homepage-cms-save-button"
        >
          <Save size={16} className="mr-2" />
          {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ التغييرات', 'Save Changes')}
        </Button>
      </div>

      {/* Hero */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="homepage-cms-hero-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <LayoutGrid size={18} className="text-[#22c8b8]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('البانر الرئيسي (Hero)', 'Hero Section')}</h3>
        </div>
        <BilingualField
          label={t('العنوان الرئيسي', 'Main Title')}
          value={state.hero.title}
          onChange={(v) => updateHero('title', v)}
          testIdBase="hero-title"
        />
        <BilingualField
          label={t('العنوان الفرعي', 'Subtitle')}
          value={state.hero.subtitle}
          onChange={(v) => updateHero('subtitle', v)}
          testIdBase="hero-subtitle"
        />
        <BilingualField
          label={t('نص زر الدعوة', 'Call-to-Action Button Text')}
          value={state.hero.cta_text}
          onChange={(v) => updateHero('cta_text', v)}
          testIdBase="hero-cta-text"
        />
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-semibold text-slate-700">{t('رابط الزر', 'CTA Link (route)')}</Label>
            <Input
              value={state.hero.cta_link || ''}
              onChange={(e) => updateHero('cta_link', e.target.value)}
              placeholder="/subscriptions"
              data-testid="hero-cta-link"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">{t('رابط صورة الطبيب', 'Doctor Photo URL')}</Label>
            <Input
              value={state.hero.doctor_photo_url || ''}
              onChange={(e) => updateHero('doctor_photo_url', e.target.value)}
              placeholder="https://..."
              data-testid="hero-doctor-photo-url"
              className="mt-1"
            />
          </div>
        </div>
      </Card>

      {/* Offer banner */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="homepage-cms-offer-card">
        <div className="flex items-center justify-between pb-2 border-b">
          <div className="flex items-center gap-2">
            <Tag size={18} className="text-[#f59e0b]" />
            <h3 className="text-lg font-extrabold text-slate-800">{t('بانر العرض', 'Promotional Offer Banner')}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">{t('مفعّل', 'Enabled')}</Label>
            <Switch
              checked={state.offer.enabled}
              onCheckedChange={(v) => updateOffer('enabled', v)}
              data-testid="offer-enabled-toggle"
            />
          </div>
        </div>
        <BilingualField
          label={t('شارة العرض', 'Badge Label')}
          value={state.offer.badge}
          onChange={(v) => updateOffer('badge', v)}
          testIdBase="offer-badge"
        />
        <BilingualField
          label={t('العنوان الرئيسي', 'Headline')}
          value={state.offer.headline}
          onChange={(v) => updateOffer('headline', v)}
          testIdBase="offer-headline"
        />
        <BilingualField
          label={t('الوصف', 'Description')}
          value={state.offer.description}
          onChange={(v) => updateOffer('description', v)}
          testIdBase="offer-description"
        />
        <BilingualField
          label={t('نص زر الدعوة', 'CTA Text')}
          value={state.offer.cta_text}
          onChange={(v) => updateOffer('cta_text', v)}
          testIdBase="offer-cta-text"
        />
        <div className="grid md:grid-cols-2 gap-4">
          <BilingualField
            label={t('كلمة "فقط"', '"Only" label')}
            value={state.offer.only_label}
            onChange={(v) => updateOffer('only_label', v)}
            testIdBase="offer-only-label"
          />
          <BilingualField
            label={t('كلمة "شهر"', '"month" label')}
            value={state.offer.period_label}
            onChange={(v) => updateOffer('period_label', v)}
            testIdBase="offer-period-label"
          />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm font-semibold text-slate-700">{t('السعر', 'Price')}</Label>
            <Input
              value={state.offer.price || ''}
              onChange={(e) => updateOffer('price', e.target.value)}
              placeholder="$20"
              data-testid="offer-price"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">{t('السعر الأصلي', 'Original Price')}</Label>
            <Input
              value={state.offer.original_price || ''}
              onChange={(e) => updateOffer('original_price', e.target.value)}
              placeholder="$30"
              data-testid="offer-original-price"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">{t('رابط الزر', 'CTA Link')}</Label>
            <Input
              value={state.offer.cta_link || ''}
              onChange={(e) => updateOffer('cta_link', e.target.value)}
              placeholder="/subscriptions"
              data-testid="offer-cta-link"
              className="mt-1"
            />
          </div>
        </div>
      </Card>

      {/* Section headings */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="homepage-cms-sections-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <LayoutGrid size={18} className="text-[#1565c0]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('عناوين الأقسام', 'Section Headings')}</h3>
        </div>
        <BilingualField
          label={t('عنوان قسم الخدمات', 'Services Section Heading')}
          value={state.sections.services_heading}
          onChange={(v) => updateSection('services_heading', v)}
          testIdBase="section-services-heading"
        />
        <BilingualField
          label={t('عنوان قسم "لماذا طبابة"', '"Why Tababa" Heading')}
          value={state.sections.why_heading}
          onChange={(v) => updateSection('why_heading', v)}
          testIdBase="section-why-heading"
        />
        <BilingualField
          label={t('العنوان الفرعي لـ "لماذا طبابة"', '"Why Tababa" Subtitle')}
          value={state.sections.why_subtitle}
          onChange={(v) => updateSection('why_subtitle', v)}
          testIdBase="section-why-subtitle"
        />
      </Card>

      {/* Dynamic banners */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="homepage-cms-banners-card">
        <div className="flex items-center justify-between pb-2 border-b">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-[#1e88e5]" />
            <h3 className="text-lg font-extrabold text-slate-800">
              {t('اللافتات الديناميكية', 'Dynamic Banners')}
              <span className="ml-2 text-xs font-normal text-slate-500">
                ({state.banners.length})
              </span>
            </h3>
          </div>
          <Button
            type="button"
            onClick={addBanner}
            className="bg-[#22c8b8] hover:bg-[#1aa199] text-white"
            data-testid="add-banner-button"
          >
            <Plus size={16} className="mr-1" />
            {t('إضافة لافتة', 'Add Banner')}
          </Button>
        </div>

        {state.banners.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-6" data-testid="banners-empty-hint">
            {t('لا توجد لافتات. اضغط "إضافة لافتة" لإنشاء واحدة.', 'No banners yet. Click "Add Banner" to create one.')}
          </p>
        )}

        {state.banners.map((b, idx) => (
          <div
            key={b.id || idx}
            className="border border-slate-200 rounded-xl p-4 space-y-4 bg-slate-50/50"
            data-testid={`banner-editor-${idx}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-700">
                #{idx + 1}
              </span>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{t('نشط', 'Active')}</Label>
                  <Switch
                    checked={!!b.active}
                    onCheckedChange={(v) => updateBanner(idx, 'active', v)}
                    data-testid={`banner-${idx}-active-toggle`}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeBanner(idx)}
                  className="text-red-600 hover:bg-red-50"
                  data-testid={`banner-${idx}-remove`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
            <BilingualField
              label={t('العنوان', 'Title')}
              value={b.title}
              onChange={(v) => updateBanner(idx, 'title', v)}
              testIdBase={`banner-${idx}-title`}
            />
            <BilingualField
              label={t('العنوان الفرعي', 'Subtitle')}
              value={b.subtitle}
              onChange={(v) => updateBanner(idx, 'subtitle', v)}
              testIdBase={`banner-${idx}-subtitle`}
            />
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-700">{t('رابط الصورة', 'Image URL')}</Label>
                <Input
                  value={b.image_url || ''}
                  onChange={(e) => updateBanner(idx, 'image_url', e.target.value)}
                  placeholder="https://..."
                  data-testid={`banner-${idx}-image-url`}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">{t('الترتيب', 'Order')}</Label>
                <Input
                  type="number"
                  value={b.order ?? 0}
                  onChange={(e) => updateBanner(idx, 'order', parseInt(e.target.value, 10) || 0)}
                  data-testid={`banner-${idx}-order`}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700">{t('رابط عند الضغط (اختياري)', 'CTA Link (optional)')}</Label>
              <Input
                value={b.cta_link || ''}
                onChange={(e) => updateBanner(idx, 'cta_link', e.target.value)}
                placeholder="/subscriptions"
                data-testid={`banner-${idx}-cta-link`}
                className="mt-1"
              />
            </div>
            {b.image_url && (
              <div className="rounded-lg overflow-hidden border">
                <img
                  src={b.image_url}
                  alt="preview"
                  className="w-full h-32 object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        ))}
      </Card>

      {/* Bottom save button for long-form convenience */}
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1565c0] hover:bg-[#0d47a1] text-white"
          data-testid="homepage-cms-save-button-bottom"
        >
          <Save size={16} className="mr-2" />
          {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ التغييرات', 'Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default AdminHomepageView;
